<template>
  <div class="kpt-group-create">
    <vue-headful title="Create Group" />
    <header class="kpt-detail-pane-header">
      <h4>Create Group</h4>
      <div class="page-control">
        <font-awesome-icon :icon="['far', 'times-circle']" size="lg" @click="close" />
      </div>
    </header>
    <div class="kpt-create-steps">

      <b-tabs v-model="activeTab" class="is-fullheight" @input="validate">
        <b-tab-item label="Detail">
          <p>Create a Group to share entities with an Account.</p>
          <br />
          <div class="kpt-field-container">
            <b-field label="Group Owner" horizontal left required>
              <b-select v-model="groupToCreate.ownerAccountId" @input="groupOwnerSelected" required>
                <option v-if="accounts.length == 1" :value="user.accountId">{{user.accountName}}</option>
                <option
                  v-else
                  v-for="option in accounts"
                  :value="option.id"
                  :key="option.id">
                  {{ option.name }}
                </option>
              </b-select>
            </b-field>
          </div>
          <hr>
          <div class="kpt-field-container">
            <b-field label="Name" horizontal required left>
              <b-input type="text" spellcheck="false" autocomplete="off" placeholder="Group Name" expanded v-model="groupToCreate.name" required></b-input>
            </b-field>
          </div>
          <div class="kpt-field-container">
            <b-field label="Entity Type" horizontal required left>
              <b-select v-model="groupToCreate.entityType" placeholder="Select an entity type" @input="resetEntities" expanded required>
                <option
                    v-for="option in groupEntityTypes"
                    :value="option.name"
                    :key="option.name">
                    {{ option.displayName }}
                </option>
              </b-select>
            </b-field>
          </div>
          <div class="kpt-item-selector">
            <b-field label="Entities" horizontal left>
              <div v-if="groupToCreate.entityType">
                <entity-selector ref="groupEntitySelector" 
                  :group-entity-type="groupToCreate.entityType" 
                  @entity-selected="entitySelected" 
                  :pre-selected="preSelectedEntities"
                  :group-owner="groupToCreate.ownerAccountId"></entity-selector>
              </div>
              <div v-else class="has-text-grey">Entity selection will be available once you have selected an Entity Type.</div>
            </b-field>
          </div>
        </b-tab-item>
        <b-tab-item label="Share">
          <div class="kpt-item-selector">
            <b-field label="Share With" horizontal left>
              <account-selector ref="shareWithAccountSelect" @account-selected="sharedAccountSelected" :can-select="canSelectAccount" :exclude-accounts="groupToCreate.ownerAccountId ? [groupToCreate.ownerAccountId] : []"></account-selector>
            </b-field>
          </div>
          <b-message type="is-info" class="mt-4">
            OR enter an email address and we will attempt to match the address with an existing Account. If no match is found, the recipient will be invited to establish an account with the Kymeta Access Portal via email.
          </b-message>
          <div class="kpt-field-container">
            <b-field label="Email Address"
              :type="{'is-danger' : validationErrors.includes('Invalid Email Address')}" 
              :message="{'This email is invalid.' : validationErrors.includes('Invalid Email Address')}" horizontal required left>
              <b-input 
                type="email" 
                placeholder="Email address..." 
                expanded 
                v-model="groupToCreate.inviteEmailAddress" 
                @input="emailInputReceived" 
                :disabled="!!groupToCreate.sharedWith.length"></b-input>
            </b-field>
          </div>
        </b-tab-item>
        <b-tab-item label="Confirmation">
          <b-notification class="kpt-confirmation-validation" type="is-danger is-light" role="alert" :closable="false" icon-pack="far" :active="isInvalid">
            <p>
              <strong>Please review the following errors:</strong>
              <ul>
                <li v-for="(err, idx) in validationErrors" :key="idx">
                  {{ err }}
                </li>
              </ul>
            </p>
          </b-notification>
          <div class="kpt-group-create-confirmation">
            <header>
              <h6 class="is-uppercase"><strong>Details</strong></h6>
            </header>
            <b-field label="Name" horizontal left class="my-1">
              {{ groupToCreate.name || '-' }}
            </b-field>
            <b-field label="Group Owner" horizontal left class="my-1">
              {{ getAccountName(groupToCreate.ownerAccountId) }}
            </b-field>
            <b-field label="Shared With" horizontal left class="my-1">
              <div v-if="groupToCreate.inviteEmailAddress">
                {{ groupToCreate.inviteEmailAddress }}
              </div>
              <div v-else>
                <span class="is-capitalized">{{ getAccountName(groupToCreate.sharedWith[0]) }}</span>
              </div>
            </b-field>
            <b-field label="Entity Type" horizontal left class="my-1">
              <span class="is-capitalized">{{ groupToCreate.entityType || '-' }}</span>
            </b-field>
            <b-message type="is-info" class="mt-4" v-if="groupToCreate.inviteEmailAddress">
              You have elected to share this group with an email address. We will attempt to match the email address with an existing Account.
              If no match is found, we will send an email to <span class="has-text-weight-bold">'{{ groupToCreate.inviteEmailAddress }}'</span>, inviting them to the Kymeta Access Portal.
            </b-message>
            <header>
              <h6 class="is-uppercase mt-4"><strong>{{ groupToCreate.entityType ? `${groupToCreate.entityType}s` : 'Entities' }}</strong></h6>
            </header>
            <div v-if="groupToCreate.entities && !!groupToCreate.entities.length">
              <group-entities :entities="groupToCreate.entities" :group-entity-type="groupToCreate.entityType"></group-entities>
            </div>
            <div v-else>
              <p class="mt-1">No entities have been selected.</p>
            </div>
          </div>
        </b-tab-item>
      </b-tabs>
    </div>
    <footer class="kpt-create-navigation">
      <div class="buttons">
        <b-button size="is-small" type="is-primary is-outlined" @click="close" v-if="activeTab == 0">Cancel</b-button>
        <b-button size="is-small" type="is-primary is-outlined" @click="goBack" v-if="activeTab > 0">Back</b-button>
        <b-button size="is-small" type="is-primary is-outlined" @click="goNext" v-if="activeTab <= 1" :disabled="isNextButtonDisabled">Next</b-button>
        <b-button size="is-small" type="is-primary is-outlined" @click="create" :disabled="isCreating || isInvalid" :loading="isCreating" v-if="activeTab == 2">Create Group</b-button>
      </div>
    </footer>
  </div>
</template>
<script>
import { orderBy, uniqBy } from 'lodash';
import { idToNameParserMixin } from '../../shared/mixins/idToNameParser';
import AccountsSelectorComponent from '../../accounts/components/AccountsSelectorComponent.vue';
import GroupEntitySelectorComponent from '../components/GroupEntitySelectorComponent.vue';
import GroupEntitiesComponent from '../components/GroupEntitiesComponent.vue';

export default {
  data() {
    return {
      activeTab: 0,
      groupToCreate: {
        name: null,
        ownerAccountId: null,
        entityType: null,
        inviteEmailAddress: null,
        entities: [],
        sharedWith: []
      },
      preSelectedEntities: [],
      canSelectAccount: true,
      isCreating: false,
      validationErrors: [],
      validationTimeout: null
    }
  },
  components: {
    'account-selector': AccountsSelectorComponent,
    'entity-selector': GroupEntitySelectorComponent,
    'group-entities': GroupEntitiesComponent
  },
  mixins: [idToNameParserMixin],  
  mounted() {
    if (this.$route.params) {      
      // check for a pre-selected account
      if (this.$route.params.groupOwnerId) { 
        this.groupToCreate.ownerAccountId = this.$route.params.groupOwnerId; 
      } else { 
        this.groupToCreate.ownerAccountId = this.user.accountId; 
      }
      // check for pre-selected entity type
      if (this.$route.params.entityType) this.groupToCreate.entityType = this.$route.params.entityType;
      
      // check for pre-selected entity metadata
      if (this.preSelectedEntity) {
        this.groupToCreate.entities.push(this.preSelectedEntity);
        this.preSelectedEntities.push(this.preSelectedEntity);
      }
    } else {
      // no route params found, so just use the logged in users accountId as the default for the group owner
      this.groupToCreate.ownerAccountId = this.user.accountId; 
    }
  },
  methods: {
    close() {
      this.$router.push({ path: '/groups' });
    },
    goNext() {
      this.validate();
      if (this.activeTab == 2) return;
      if (this.activeTab == 0 && !this.isDetailComplete) return;
      this.activeTab++;
    },
    goBack() {
      this.validate();
      if (this.activeTab == 0) return;
      this.activeTab--;
    },
    validate() {
      this.validationErrors = [];
      if (!this.groupToCreate.name?.trim()) this.validationErrors.push('Name is required'); // validate if " "(only space) provided as GroupName
      if (!this.groupToCreate.ownerAccountId) this.validationErrors.push('Group Owner is required');
      if (!this.groupToCreate.sharedWith.length && !this.groupToCreate.inviteEmailAddress) this.validationErrors.push('Shared With is required');
      if (!this.groupToCreate.entityType) this.validationErrors.push('Entity Type is required');
      if (this.groupToCreate.inviteEmailAddress && !this.testEmailAddressValidity(this.groupToCreate.inviteEmailAddress)) this.validationErrors.push('Invalid Email Address');
    },
    testEmailAddressValidity(emailAddress) {
      const re = /^(([^<>()[\]\.,;:\s@\"]+(\.[^<>()[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/i;
      return re.test(emailAddress);
    },
    async create() {
      // clone the object
      const payload = { ...this.groupToCreate }
      
      // flatten entities to just an array of their id's
      if (payload.entities) payload.entities = payload.entities.map(e => e.id);

      // fire off the request to create the group
      this.isCreating = true;
      const result = await this.$store.dispatch('addGroup', payload);
      this.isCreating = false;
      if (!result.isSuccess) {
        // toast error for user
        this.$buefy.toast.open({
          duration: 3000,
          message: result.err?.message || `Failed to add Group (${payload.name}).`,
          type: 'is-danger'
        });
        return;
      }

      if (this.$hasRole('Super User,Kymeta Admin,Sales Support,Product Support,Account Admin,Credentialed')) {
        // group was created successfully, route the user to the group overview detail page
        this.$router.push({ name: 'groupOverview', params: { id: this.$store.state.groups.group.id } });
      } else {
        this.$router.push({ name: 'Groups' });
      }
    },
    groupOwnerSelected() {
      this.resetEntities();
      // refresh list of accounts to ensure group owner is not available in shared with selection
      this.$refs.shareWithAccountSelect.refreshAccounts();

      if (this.groupToCreate.entityType) {
        // refresh the list of available entities based on selected owner
        this.$refs.groupEntitySelector.refreshEntities();
      }
    },
    resetEntities() {
      this.groupToCreate.entities = [];
      // clear the selected entities so the tags are removed as well
      if (this.$refs.groupEntitySelector) this.$refs.groupEntitySelector.selectedEntities = [];
    },
    entitySelected(entities) {
      this.groupToCreate.entities = entities;
    },
    sharedAccountSelected(accounts) {
      // should not be possible, but null out the email address value if accounts are selected
      if (accounts && !!accounts.length) this.groupToCreate.inviteEmailAddress = null;
      this.groupToCreate.sharedWith = accounts.map(a => a.id);
    },
    emailInputReceived(value) {
      if (!value) {
        // no email address provided, so we allow account selection
        this.canSelectAccount = true;
      } else {
        // an email address value is present, disable account selection
        this.canSelectAccount = false;
        this.groupToCreate.sharedWith = [];
      }
      if (this.validationTimeout) clearTimeout(this.validationTimeout);
      this.validationTimeout = setTimeout(this.validate, 500); // delay validation of email
    },
    getAccountName(id) {
      const account = this.allAccounts.find(a => a.id == id);
      return account ? account.name : '-';
    }
  },
  computed: {
    user() { return this.$store.state.user.user },
    accounts() { return orderBy(this.$store.state.accounts.accounts, ['name'], ['asc']); },
    sharedAccounts() { return orderBy(this.$store.state.accounts.sharedAccounts, ['name'], ['asc']); },
    allAccounts() {
      // join accounts and sharedAccounts arrays
      const concatAccounts = this.accounts.concat(this.sharedAccounts);
      // return unique to prevent duplicates
      return uniqBy(concatAccounts, 'id');
    },
    preSelectedEntity() {
      return this.$store.state.groups.preSelectedEntity;
    },
    isInvalid() { return this.validationErrors.length > 0; },
    isDetailComplete() {
      return this.groupToCreate.name 
        && this.groupToCreate.entityType
        && this.groupToCreate.ownerAccountId
    },
    isShareWithComplete() {
      return (this.groupToCreate.sharedWith && !!this.groupToCreate.sharedWith.length) || !!this.groupToCreate.inviteEmailAddress;
    },
    isNextButtonDisabled() {
      if (this.activeTab == 0 && !this.isDetailComplete) return true;
      if (this.activeTab == 1 && !this.isShareWithComplete) return true;
      return false;
    },
    groupEntityTypes() {
      return [
        { name: 'remote', displayName: 'Remote' },
        { name: 'device', displayName: 'Device' },
        { name: 'subscription', displayName: 'Subscription' }
      ]
    }
  }
}
</script>
<style lang="scss" scoped>
.kpt-group-create {
  height: 100%;
  display: flex;
  flex-direction: column;

  .kpt-create-steps {
    flex: 1 1 auto;
    overflow-y: auto;
  }

  .kpt-field-container {
    width: 100%;
    max-width: 400px;
    margin-bottom: 8px;

    @media screen and (min-width: 1441px) {
      width: 50%;
      max-width: 600px;
    }
  }

  .kpt-item-selector {
    ::v-deep .field.is-horizontal .field-label {
      width: 150px;
      max-width: 150px;
    }
  }
  ::v-deep .kpt-group-entities-footer {
    bottom: 8px;
  }

  .kpt-group-create-confirmation {
    ::v-deep .field-label {
      max-width: 160px;
    }
  }

  .kpt-create-navigation {
    height: 52px;
  }

  .kpt-confirmation-validation {
    ul {
      list-style: circle;

      li {
        margin-left: 20px;
      }
    }
  }
}

</style>
